import React from 'react'

import AttackTypeIcon from 'components/common/AttackTypeIcon'
import { SmallBadge } from 'components/common/Badge'
import GlyphIcon from 'components/common/GlyphIcon'
import Text from 'components/common/Text'
import { getScenarioCommands } from 'components/scenarios/utils'
import {
  getGraphNodesByType,
  scenarioGraphTypes
} from 'components/scenarios/utils/graph'
import theme from 'theme'
import { AttackCommand } from 'types/api/attacks/details/targeting'
import { Scenario } from 'types/api/scenarios/scenario'
import { unique } from 'utils/list'
import { getFormattedAttackType } from 'utils/metadata'

export const TEXT_LIMIT = 2
export const ICON_LIMIT = 4

export function getUniqueScenarioCommands(scenario) {
  const statusChecks = getGraphNodesByType(
    scenario.graph,
    scenarioGraphTypes.SynchronousStatusCheck,
    scenarioGraphTypes.ContinuousStatusCheck
  ).map(() => 'status_check')
  const commands = getScenarioCommands(scenario)
  return unique([...statusChecks, ...commands])
}

const ScenarioCommandIconsList: React.FC<{
  iconLimit?: number
  uniqueNodeCommands?: AttackCommand[]
  iconSize?: number
  fontSize?: number
}> = ({
  iconLimit = ICON_LIMIT,
  uniqueNodeCommands,
  iconSize = theme.space[4],
  fontSize = theme.fontSizes[1]
}) => {
  const overIconLimit = uniqueNodeCommands.length > iconLimit
  return (
    <div
      css={{
        display: 'inline-flex',
        flexDirection: 'row-reverse',
        color: theme.colors.steel[1]
      }}
    >
      {overIconLimit && (
        <SmallBadge
          css={{
            alignSelf: 'flex-start',
            background: theme.colors.steel[1],
            marginLeft: -theme.space[2],
            borderRadius: theme.borderRadius[1],
            paddingLeft: theme.space[2],
            paddingRight: theme.space[2]
          }}
        >
          +{uniqueNodeCommands.length - iconLimit}
        </SmallBadge>
      )}
      {uniqueNodeCommands
        .slice(0, iconLimit)
        .reverse()
        .map((command, i) => {
          return (
            <GlyphIcon
              key={command + i}
              backgroundColor={theme.colors.white[0]}
              css={{
                width: iconSize,
                height: iconSize,
                marginLeft: -theme.space[2],
                '& .glyph-circle': {
                  border: theme.border.base,
                  borderColor: theme.colors.gray[2]
                },
                '&:last-of-type': {
                  marginLeft: 0
                }
              }}
            >
              <AttackTypeIcon
                css={{
                  fontSize: fontSize,
                  color: theme.colors.steel[1]
                }}
                icon={command}
              />
            </GlyphIcon>
          )
        })}
    </div>
  )
}

const ScenarioAttackTypeList: React.FC<{ scenario: Scenario }> = ({
  scenario
}) => {
  const uniqueNodeCommands = getUniqueScenarioCommands(scenario)

  if (uniqueNodeCommands.length < 1) {
    return null
  }

  return (
    <div css={{ display: 'flex', alignItems: 'center' }}>
      <ScenarioCommandIconsList uniqueNodeCommands={uniqueNodeCommands} />
      {uniqueNodeCommands.length <= TEXT_LIMIT && (
        <Text as="h5" color="normal" css={{ marginLeft: theme.space[2] }}>
          {uniqueNodeCommands.map(getFormattedAttackType).join(' + ')}
        </Text>
      )}
    </div>
  )
}
export { ScenarioCommandIconsList, ScenarioAttackTypeList }
